import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Calendar, CreditCard, User, Receipt, ShieldAlert, CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import Card from '../components/ui/Card';
import { fetchPaymentsHistory } from "../apiHelper";

const History = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadPaymentsHistory = async () => {
            setLoading(true);
            try {
                // Invoking backend query handler (accepts no parameters)
                const data = await fetchPaymentsHistory();
                // console.log(data)
                setTransactions(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error fetching payment statement history:", err);
                showToast(err.response?.data?.detail || "Could not retrieve historical payment records.", "error");
            } finally {
                setLoading(false);
            }
        };

        loadPaymentsHistory();
    }, [showToast]);

    // Helper to format Paystack subunits safely (kobo/cents to standard format)
    const formatCurrency = (amountInSubunits, currencyCode = "NGN") => {
        const amount = (amountInSubunits || 0);// / 100;
        const symbolMap = { NGN: "₦", USD: "$", GBP: "£", EUR: "€" };
        const symbol = symbolMap[currencyCode?.toUpperCase()] || (currencyCode || "NGN") + " ";
        return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Maps API structural engine variables to human-readable design states
    const getStatusConfig = (status) => {
        const normalized = status?.toLowerCase();
        if (normalized === 'success' || normalized === 'successful' || normalized === 'approved') {
            return {
                text: "Successful",
                icon: <CheckCircle2 size={14} className="text-emerald-600" />,
                classes: "bg-emerald-50 text-emerald-700 border-emerald-200"
            };
        }
        if (normalized === 'failed' || normalized === 'declined') {
            return {
                text: "Failed",
                icon: <XCircle size={14} className="text-rose-600" />,
                classes: "bg-rose-50 text-rose-700 border-rose-200"
            };
        }
        return {
            text: status || "Pending",
            icon: <AlertCircle size={14} className="text-amber-600" />,
            classes: "bg-amber-50 text-amber-700 border-amber-200"
        };
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[50vh] gap-3">
                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                <p className="text-sm font-medium text-zinc-500">Retrieving settlement records...</p>
            </div>
        );
    }

    return (
        <div className="p-6 sm:p-8 max-w-5xl mx-auto space-y-6">

            {/* Top Navigation Row */}
            <div className="flex items-center justify-between border-b border-zinc-100 pb-4">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900 transition-colors cursor-pointer"
                >
                    <ArrowLeft size={16} /> Back
                </button>
            </div>

            {/* Header Description */}
            <div className="space-y-1">
                <h1 className="text-2xl font-extrabold text-zinc-900 tracking-tight flex items-center gap-2">
                    <Receipt className="text-zinc-500" size={24} /> Payment Audit Log
                </h1>
                <p className="text-sm text-zinc-500">
                    A comprehensive overview of your secure portal transactions and gateway settlements.
                </p>
            </div>

            {/* Main History Feed Stack */}
            {transactions.length === 0 ? (
                <Card className="p-8 border-dashed border-zinc-300 bg-zinc-50 flex flex-col items-center justify-center text-center">
                    <ShieldAlert size={36} className="text-zinc-400 mb-2" />
                    <p className="text-zinc-500 text-sm font-medium">No historical transaction operations found for this profile.</p>
                </Card>
            ) : (
                <div className="space-y-4">
                    {transactions.map((tx, idx) => {
                        const statusStyle = getStatusConfig(tx.status);
                        return (
                            <div
                                key={tx.internal_reference || idx}
                                className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden hover:border-zinc-300 transition-all"
                            >
                                {/* Transaction Card Header Summary */}
                                <div className="p-4 sm:p-5 border-b border-zinc-100 bg-zinc-50/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    {/* <div className="space-y-1">
                                        <div className="flex items-center gap-2.5 flex-wrap">
                                            <span className="text-xs font-mono font-bold text-zinc-400 uppercase tracking-tight">Ref:</span>
                                            <h3 className="text-xs font-mono font-bold text-zinc-800">
                                                {tx.paystack_transaction_reference || tx.internal_reference}
                                            </h3>
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-md border ${statusStyle.classes}`}>
                                                {statusStyle.icon}
                                                {statusStyle.text}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-zinc-400 font-medium">
                                            System ID: Account #{tx.customer_id} • Org Profile #{tx.organization_id}
                                        </p>
                                    </div> */}
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2.5 flex-wrap">
                                            <span className="text-xs font-mono font-bold text-zinc-400 uppercase tracking-tight">Ref:</span>
                                            <h3 className="text-xs font-mono font-bold text-zinc-800">
                                                {tx.paystack_transaction_reference || tx.internal_reference}
                                            </h3>
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-md border ${statusStyle.classes}`}>
                                                {statusStyle.icon}
                                                {statusStyle.text}
                                            </span>
                                        </div>
                                        {/* Refactored to feature Organization Name cleanly */}
                                        <p className="text-[11px] text-zinc-500 font-semibold tracking-tight">
                                            {tx.organization_name || `Organization ID: #${tx.organization_id}`}
                                        </p>
                                    </div>
                                    <div className="sm:text-right">
                                        <span className="text-base font-black text-zinc-950">
                                            {formatCurrency(tx.amount, tx.currency)}
                                        </span>
                                    </div>
                                </div>

                                {/* Detailed Parameters Body */}
                                {/* <div className="p-4 sm:p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                                    <div className="flex items-center gap-2 text-zinc-600">
                                        <Calendar size={14} className="text-zinc-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-[10px] text-zinc-400 uppercase font-bold">Settled On</p>
                                            <p className="font-semibold text-zinc-800">
                                                {tx.paid_at
                                                    ? new Date(tx.paid_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                                                    : 'Unconfirmed'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 text-zinc-600">
                                        <CreditCard size={14} className="text-zinc-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-[10px] text-zinc-400 uppercase font-bold">Channel</p>
                                            <p className="font-semibold text-zinc-800 uppercase tracking-wide">
                                                {tx.paystack_channel || 'N/A'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 text-zinc-600">
                                        <Receipt size={14} className="text-zinc-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-[10px] text-zinc-400 uppercase font-bold">Webhook Confirmed</p>
                                            <p className="font-semibold text-zinc-800">
                                                {tx.amount_received_from_webhook
                                                    ? formatCurrency(tx.amount_received_from_webhook, tx.currency)
                                                    : '0.00'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="col-span-2 md:col-span-1 flex items-center gap-2 text-zinc-600">
                                        <div className="w-full">
                                            <p className="text-[10px] text-zinc-400 uppercase font-bold">Gateway Response</p>
                                            <p className="font-medium text-zinc-700 italic truncate max-w-[200px]" title={tx.paystack_gateway_response}>
                                                {tx.paystack_gateway_response || 'No feedback logged'}
                                            </p>
                                        </div>
                                    </div>
                                </div> */}
                                {/* Detailed Parameters Body */}
                                <div className="p-4 sm:p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                                    <div className="flex items-center gap-2 text-zinc-600">
                                        <Calendar size={14} className="text-zinc-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-[10px] text-zinc-400 uppercase font-bold">Settled On</p>
                                            <p className="font-semibold text-zinc-800">
                                                {tx.paid_at
                                                    ? new Date(tx.paid_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                                                    : 'Unconfirmed'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 text-zinc-600">
                                        <CreditCard size={14} className="text-zinc-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-[10px] text-zinc-400 uppercase font-bold">Channel</p>
                                            <p className="font-semibold text-zinc-800 uppercase tracking-wide">
                                                {tx.paystack_channel || 'N/A'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Swapped Webhook Field to explicitly feature the Ward's Customer Identity */}
                                    <div className="col-span-2 md:col-span-2 flex items-center gap-2 text-zinc-600">
                                        <User size={14} className="text-zinc-400 flex-shrink-0" />
                                        <div>
                                            <p className="text-[10px] text-zinc-400 uppercase font-bold">Ward Account</p>
                                            <p className="font-bold text-indigo-600 truncate max-w-[280px]">
                                                {tx.customer_name || `ID Ref: #${tx.customer_id}`}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default History;